// Delegation modal — internal user or external email
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { X, Send, UserPlus, Mail, Loader2, CheckCircle } from 'lucide-react'
import { useExpertAssignment } from '@/hooks/useExpertAssignment'
import { supabase } from '@/lib/supabase'
import { useQuery } from '@tanstack/react-query'

interface DelegationDialogProps {
  issueId: string
  onClose: () => void
}

export function DelegationDialog({ issueId, onClose }: DelegationDialogProps): JSX.Element {
  const { delegateByEmail, delegateToUser, isDelegating } = useExpertAssignment()
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [selectedUser, setSelectedUser] = useState('')
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const usersQuery = useQuery({
    queryKey: ['org-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, email')
        .eq('is_active', true)
        .order('full_name')
      if (error) throw error
      return data ?? []
    },
  })

  const handleDelegateInternal = async () => {
    if (!selectedUser) return
    setError(null)
    try {
      await delegateToUser({ issueId, userId: selectedUser })
      setSuccess('Issue delegated to user.')
      setTimeout(onClose, 1500)
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  const handleDelegateExternal = async () => {
    if (!email.trim()) return
    setError(null)
    try {
      await delegateByEmail({
        issueId,
        email: email.trim(),
        message: message.trim() || `You have been assigned an issue. Please review it.`,
      })
      setSuccess('Delegation email sent.')
      setTimeout(onClose, 1500)
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-kaycoo-charcoal/50 backdrop-blur-sm">
      <Card className="w-full max-w-md border-kaycoo-teal-light bg-kaycoo-white shadow-xl">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-kaycoo-teal-dark">Delegate Issue</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {success && (
            <Alert className="mb-4 border-kaycoo-teal bg-kaycoo-teal-pale">
              <CheckCircle className="h-4 w-4 text-kaycoo-teal-dark" />
              <AlertDescription className="text-kaycoo-teal-dark">{success}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="internal">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="internal" className="gap-1.5">
                <UserPlus className="h-3.5 w-3.5" />
                Internal
              </TabsTrigger>
              <TabsTrigger value="external" className="gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                External
              </TabsTrigger>
            </TabsList>

            <TabsContent value="internal" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Select User</Label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {(usersQuery.data ?? []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name} ({u.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleDelegateInternal}
                disabled={!selectedUser || isDelegating}
                className="w-full bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
              >
                {isDelegating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Delegate to User
              </Button>
            </TabsContent>

            <TabsContent value="external" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="delegate-email">Email Address</Label>
                <Input
                  id="delegate-email"
                  type="email"
                  placeholder="expert@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delegate-message">Message (optional)</Label>
                <Textarea
                  id="delegate-message"
                  placeholder="Additional context for the external expert..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                />
              </div>
              <Button
                onClick={handleDelegateExternal}
                disabled={!email.trim() || isDelegating}
                className="w-full bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
              >
                {isDelegating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                Send Delegation Email
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
